// Import sessions (design 1c) — contract: backend/docs/IMPORT_SESSIONS.md.
import { api } from '../api/client';
import type { ImportTypeCode, PayMode } from '@rimon/contracts';

export type ReviewState = 'open' | 'later' | 'confirmed';
export interface VatLine { rate?: string; net?: string; vat?: string }
export interface Escompte { rate?: string; until?: string; amount?: string; source?: 'invoice' | 'emetteur'; applyAnyway?: boolean }
export interface DraftFields {
  partyName?: string; partyId?: string; number?: string; date?: string; period?: string; dueDate?: string;
  lines?: VatLine[]; gross?: string; amount?: string; communication?: string; escompte?: Escompte | null;
  /** FP (30.10.2026): gross and net of the fiche; `amount` = „À verser“ (the transfer). */
  grossSalary?: string; netSalary?: string;
  creditForId?: string; cardLast4?: string; account?: 'BIL' | 'BGL' | 'VISA_BIL' | 'VISA_BGL';
  /** BE: bank of the VISA card; derived from the card when it is known. */
  paidVia?: 'BIL' | 'BGL'; cardOwner?: string;
  /** FR: „Montant à payer“ – how it is chosen and the amount (CUSTOM: typed). */
  toPay?: { mode: PayMode; amount?: string; chosen?: boolean } | null;
}
export interface DraftDocument {
  id: string; type: ImportTypeCode; pageFrom: number; pageTo: number; reviewState: ReviewState;
  fields: DraftFields; fieldSource: Record<string, 'ai' | 'user' | 'core'>; duplicateOf: string | null;
  /** 0–1 per AI-filled field; below `confident` the field is marked "prüfen". */
  fieldConfidence?: Record<string, number>;
}
export interface SessionFile {
  id: string; fileId: string; name: string; sourceZip: string | null; pages: number; sizeBytes: string;
  type: ImportTypeCode | null; typeConfidence: number | null; documents: DraftDocument[];
  aiStatus?: 'pending' | 'done' | 'failed' | null;
  aiSuggestion?: { type: ImportTypeCode; confidence: number | null; via: 'text' | 'ocr' | 'vision' | 'rules' | 'none' } | null;
}
export interface ImportSession {
  id: string; status: 'uploading' | 'sorting' | 'reviewing' | 'done' | 'draft' | 'discarded'; step: number; mode: 'manual' | 'ai';
  version: number; createdAt: string; updatedAt: string; files: SessionFile[];
  skipped: { name: string; sourceZip?: string | null; reason: 'unsupported' | 'duplicate_file' }[];
  lastImport: { at: string; documentIds: string[] } | null;
  /** Set when another user holds the edit lock (heartbeat younger than 2 minutes). */
  lock?: SessionLock | null;
  /** Files the local AI is still reading. */
  aiPending?: number;
}
export interface SessionLock { userId: string; name: string; at: string }
export interface SessionSummary {
  id: string; status: ImportSession['status']; step: number; mode: ImportSession['mode'];
  firstFileName: string | null; fileCount: number; documentCount: number; confirmedCount: number; unclearCount: number; aiPending?: number;
  escompteSoon: { count: number; minDays: number } | null; lock: SessionLock | null;
  createdAt: string; updatedAt: string; createdBy?: { name: string }; updatedBy?: { id: string; name: string } | null;
}

const P = '/compta/import-sessions';
export const createSession = (mode: 'manual' | 'ai') => api<ImportSession>(P, { method: 'POST', body: { mode } });
export const listSessions = () => api<SessionSummary[]>(P);
export const getSession = (id: string) => api<ImportSession>(P + '/' + id);
export function uploadFiles(id: string, files: File[]) {
  const fd = new FormData();
  files.forEach((f) => fd.append('files', f));
  return api<ImportSession>(P + '/' + id + '/files', { method: 'POST', body: fd });
}
export const removeFile = (id: string, fileId: string) => api<ImportSession>(P + '/' + id + '/files/' + fileId, { method: 'DELETE' });
export const setTypes = (id: string, fileIds: string[], type: ImportTypeCode) => api<ImportSession>(P + '/' + id + '/types', { method: 'PATCH', body: { fileIds, type } });
export const startReview = (id: string) => api<ImportSession>(P + '/' + id + '/review', { method: 'POST' });
export const patchDocument = (id: string, documentId: string, body: { fields?: DraftFields; reviewState?: ReviewState; acceptDuplicate?: boolean }) =>
  api<DraftDocument>(P + '/' + id + '/documents/' + documentId, { method: 'PATCH', body });
/** All confirmed drafts in one transaction; 409 import_incomplete while others are open/later, unless `partial`. */
export const importSession = (id: string, partial = false) => api<{ imported: number; documentIds: string[]; fileCount: number; remaining: number }>(P + '/' + id + '/import', { method: 'POST', body: partial ? { partial: true } : {} });
export const undoImport = (id: string) => api<{ removed: number }>(P + '/' + id + '/undo', { method: 'POST' });
export const keepAsDraft = (id: string) => api<ImportSession>(P + '/' + id + '/draft', { method: 'POST' });
/** Discards in any state. With imported documents: 409 session_has_imports unless `imported` says undo them or keep them. */
export const discardSession = (id: string, imported?: 'undo' | 'keep') => api<void>(P + '/' + id + '/discard', { method: 'POST', body: imported ? { imported } : {} });
export const fileUrl = (id: string, fileId: string) => api<{ url: string; expiresIn: number; mimeType: string; pages: number }>(P + '/' + id + '/files/' + fileId + '/url');
/** LP: invoices of the review whose IBAN is new or changed for a known supplier (read only). */
export const ibanChecks = (id: string) => api<Array<{ draftId: string; iban: string; ibanAlert: 'new' | 'changed' }>>(P + '/' + id + '/iban-checks');
export const heartbeat = (id: string) => api<{ lockedUntil: string }>(P + '/' + id + '/heartbeat', { method: 'POST' });
export const capabilities = () => api<{ ai: boolean; reason: 'disabled' | 'unreachable' | 'model_missing' | null; model: string | null; vision: boolean; confident: number }>(P + '/capabilities');
